<template>
  <hm-col v-if="!isNil(internalrates) && !isNil(internalrates.rates)" :id="$attrs['id']" cols="12">
    <spinner-overlay v-if="updating" />
    <hm-table :class="[{ 'table--locked': locked }]"
              :hover="false"
              min-width="550px"
              columns="5"
              grid-template-columns="minmax(9rem, 1fr) minmax(18.5rem, 4fr) minmax(16rem,3fr) minmax(18.5rem,3fr) minmax(24rem,3fr)">
      <template>
        <template slot="table-head">
          <div class="hm-table__th">{{ $t('util.rateEditor.nights.night') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.nights.date') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.nights.calculatedRate') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.nights.customRate') }}</div>
          <div class="hm-table__th text-right">{{ $t('util.rateEditor.nights.totalAmount') }}</div>
        </template>
        <template v-for="(rate, index) in internalrates.rates">
          <template v-if="index < visibleNights">
            <div class="hm-table__td" v-html="index + 1"></div>
            <div class="hm-table__td">{{ rate.effectiveDate | toDate }}</div>
            <div class="hm-table__td">{{ rate.originalAmount | toCurrency }} {{ rate.currency }}</div>
            <div class="hm-table__td">
              <hm-input-money :lazy="true"
                              v-model="rate.customAmount"
                              :name="`amount-${index+1}`"
                              :currency="rate.currency"
                              :inline="true"
                              :required="true"
                              :disabled="rate.posted === true || locked"
                              :label="$t('util.rateEditor.customAmount')"
                              :context-help="$t('util.rateEditor.customAmount_contextHelp')"
                              validate="min_value:0"
                              class="hm-input--inline-block"
                              @input="$emit('ratechange')">
                <template slot="icon-inline-right" slot-scope="slot">
                  <input-icon-button v-tooltip="{ title: $t('util.rateEditor.copyRateToFollowingNights'), delay: 250, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }" :disabled="rate.posted === true || locked" icon="copy-price" @click="copyToSubsequentNights(rate.effectiveDate, rate.customAmount)" />
                </template>
              </hm-input-money>
            </div>
            <div class="hm-table__td text-right">{{ rate.amount + rate.excludedTaxAmount | toCurrency }} {{ rate.currency }}</div>
          </template>
        </template>
        <template v-if="visibleNights < numNights">
          <hm-col key="showAllNights" :grid="false" cols="start-4 1 start-1@mobile 5@mobile">
            <hm-button class="btn--blue btn--no-margin-bottom"
                       style="max-width: 18.7rem;margin-bottom:1rem!important;width:100%;"
                       cols="12"
                       icon-left="down"
                       @click="toggleRateDetails = true">{{ $t('util.rateEditor.expand') }}</hm-button>
          </hm-col>
        </template>
        <template v-if="!isEmpty(internalrates.shoppingCart)">
          <div class="hm-table__th col-start-1">{{ $t('util.rateEditor.shoppingCartItems.count') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.shoppingCartItems.label') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.shoppingCartItems.date') }}</div>
          <div class="hm-table__th">{{ $t('util.rateEditor.shoppingCartItems.itemAmount') }}</div>
          <div class="hm-table__th text-right">{{ $t('util.rateEditor.shoppingCartItems.totalAmount') }}</div>

          <template v-for="(item, index) in internalrates.shoppingCart">
            <div class="hm-table__td">
              <hm-input-number :lazy="true"
                               v-model="item.itemCount"
                               :disabled="item.posted === true || locked"
                               :required="true"
                               :label="$t('util.rateEditor.itemCount')"
                               name="itemCount"
                               @input="$emit('ratechange')" />
            </div>
            <div v-tooltip="{ title: getShoppingCartItemLabel(item), delay: 250, placement: 'top', positionFixed: true, trigger: 'hover', touch: true }"
                 :class="[{'col-2': item.date == null}]"
                 class="hm-table__td">
              {{ getShoppingCartItemLabel(item) }}
            </div>
            <div v-if="item.date != null" class="hm-table__td hm-table__td--overflow">
              <template v-if="findHotelSettingById('inHouseServices', item.inHouseServiceId).rateFrequency === 'PerStay' && findHotelSettingById('inHouseServices', item.inHouseServiceId).rateMode === 'PerRoom'">
                <hm-input-date v-if="item.date != null"
                               v-model="item.date"
                               :inline="true"
                               :disabled="item.posted === true || locked"
                               :fixed="true"
                               :max-date="departureDate"
                               :min-date="arrivalDate"
                               :min-date-inclusive="true"
                               :label="$t('util.rateEditor.itemDate')"
                               name="itemDate"
                               cols="6" />
              </template>
            </div>
            <div class="hm-table__td">
              <hm-input-money :lazy="true"
                              v-model="item.itemPrice"
                              :name="`itemamount-${index+1}`"
                              :currency="item.currency"
                              :inline="true"
                              :required="true"
                              :disabled="item.posted === true || locked"
                              :label="$t('util.rateEditor.itemPrice')"
                              class="hm-input--inline-block"
                              @input="$emit('ratechange')">
                <template slot="icon-inline-right" slot-scope="slot">
                  <input-icon-button v-tooltip="{ title: $t('util.rateEditor.deleteShoppingCartItem'), delay: 250, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }" :disabled="item.posted === true || locked" icon="delete" @click="deleteShoppingCartItem(item)" />
                </template>
              </hm-input-money>
            </div>
            <div class="hm-table__td text-right">{{ item.itemCount * item.itemPrice * (findHotelSettingById('inHouseServices', item.inHouseServiceId).rateFrequency === 'PerNight' ? numNights : 1) + (isNil(item.unitPriceSummary) ? 0.0 : item.unitPriceSummary.excludedTaxAmount) | toCurrency }} {{ isNil(item.unitPriceSummary) ? hotelSettings.details.currency : item.unitPriceSummary.currency }}</div>
          </template>
        </template>
      </template>
      <div class="hm-table__td hm-table__td--no-hover col-3 reset-line-height">
        <span class="explanation-text" v-html="explanationText"></span>
      </div>
      <div class="hm-table__td hm-table__td--no-hover col-2 reset-line-height">
        <label-value-dotted :value="internalrates.unitPriceSummary.amount | toCurrency" :unit="internalrates.unitPriceSummary.currency" :label="$t('util.rateEditor.summary.roomAmount')" />
        <label-value-dotted v-for="tax in internalrates.unitPriceSummary.taxDetails"
                            v-if="tax.isInclusive == false"
                            :key="tax.taxId"
                            :value="tax.amount | toCurrency"
                            :unit="internalrates.unitPriceSummary.currency"
                            :label="findHotelSettingById('taxes', tax.taxId).name[$shortLocale]" />
        <label-value-dotted v-if="!isNil(internalrates.shoppingCartSummary) && internalrates.shoppingCartSummary.amount !== 0" :value="internalrates.shoppingCartSummary.amount | toCurrency" :unit="internalrates.shoppingCartSummary.currency" :label="$t('util.rateEditor.summary.additionalServices')" />
        <label-value-dotted v-for="tax in internalrates.shoppingCartSummary.taxDetails"
                            v-if="tax.isInclusive == false"
                            :key="tax.taxId"
                            :value="tax.amount | toCurrency"
                            :unit="internalrates.shoppingCartSummary.currency"
                            :label="findHotelSettingById('taxes', tax.taxId).name[$shortLocale]" />
        <label-value-dotted :value="internalrates.unitPriceSummary.amount + internalrates.unitPriceSummary.excludedTaxAmount + internalrates.shoppingCartSummary.amount + internalrates.shoppingCartSummary.excludedTaxAmount | toCurrency" :unit="internalrates.unitPriceSummary.currency" :label="$t('util.rateEditor.summary.totalAmount')" class="label-value-dotted__wrapper--sum label-value-dotted__wrapper--bold" />
      </div>
    </hm-table>
  </hm-col>
</template>

<script>

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, isEmpty, isNil, slice, startsWith, uniqBy } from 'lodash';
import SpinnerOverlay from './SpinnerOverlay';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'RateEditor',
  components: { SpinnerOverlay },
  mixins: [HotelmeisterMixin, TimerMixin],

  props: {
    rates: {
      required: true,
      type: Object,
      default: null,
    },
    arrivalDate: {
      required: true,
      type: String,
      default: null,
    },
    departureDate: {
      required: true,
      type: String,
      default: null,
    },
    locked: {
      required: false,
      type: Boolean,
      default: false,
    },
    updating: {
      required: false,
      type: Boolean,
      default: false,
    },
    explanationText: {
      required: false,
      type: String,
      default: '',
    },
  },

  data() {
    return {
      toggleRateDetails: null,
      customAmounts: {},
      internalrates: null,
      changed: false,
    };
  },

  computed: {
    numNights: function () {
      return this.internalrates.rates.length;
    },
    showRateDetails: function () {
      return this.numNights < this.visibleNights || this.toggleRateDetails === true;
    },
    visibleNights: function () {
      if (this.toggleRateDetails === true) return 9999;
      if (!isEmpty(this.internalrates.rates)) {
        if (uniqBy(this.internalrates.rates, 'posted').length > 1) return 9999;
        if (slice(this.internalrates.rates, 1, 9999).filter(it => it.customAmount === 0).length === (this.numNights - 1)) return 1;
        if (uniqBy(this.internalrates.rates, 'customAmount').length === 1) return 2;
      }
      return 3;
    },
  },

  watch: {
    rates: {
      handler: function (newVal) {
        this.internalrates = cloneDeep(newVal);
        this.$nextTick(() => {
          this.changed = false;
        });
      },
      deep: true,
    },
    internalrates: {
      handler: function (newVal) {
        this.changed = true;
      },
      deep: true,
    },
    customAmounts: {
      handler: function (newVal) {
        if (!isNil(this.internalrates) && !isNil(this.internalrates.rates)) {
          this.internalrates.rates.forEach((itRate) => {
            const customAmount = this.customAmounts[itRate.effectiveDate];
            itRate.customAmount = isNil(customAmount) ? 0 : customAmount;
          });
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    this.save(true);
  },

  created() {
    this.internalrates = cloneDeep(this.rates);
    setInterval(this.save, 150);
  },

  methods: {
    copyToSubsequentNights: function (effectiveDate, customAmount) {
      const sourceDate = this.$moment(effectiveDate, 'YYYYMMDD');
      this.internalrates.rates
        .filter(it => sourceDate.isBefore(this.$moment(it.effectiveDate, 'YYYYMMDD')))
        .forEach((it) => {
          it.customAmount = customAmount;
        });
      this.$emit('ratechange');
    },
    deleteShoppingCartItem: function (shoppingCartItem) {
      if (!isNil(this.internalrates.shoppingCart)) {
        this.internalrates.shoppingCart = this.internalrates.shoppingCart.filter(it => it !== shoppingCartItem);
        this.$emit('ratechange');
      }
    },
    hasFocus() {
      return !isNil(this.$el.querySelector('input:focus'));
    },
    save(force = false) {
      if (this.changed) {
        if (force || !this.hasFocus()) {
          this.changed = false;
          this.$emit('update:rates', this.internalrates);
        }
      }
    },
    getShoppingCartItemLabel(item) {
      let ret = '';
      const ihs = this.findHotelSettingById('inHouseServices', item.inHouseServiceId);

      if (isEmpty(item.alternativeLabel)) {
        ret += ihs.name[this.$shortLocale];
      } else {
        ret += `${item.alternativeLabel} - ${ihs.name[this.$shortLocale]}`;
      }

      if (ihs.rateFrequency === 'PerNight') {
        ret += ` (${this.$t(`RateFrequency.${ihs.rateFrequency}`)})`;
      }

      return ret;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/lib/util/RateEditor.vue